//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { HTMLAttributes, useEffect, useState } from 'react';
import { formatDate, formatTime } from '../../_utils/date';
import { useAppSelector } from '@/redux/hooks';
import { selectAuthLoading } from '@/redux/selectors/authSelectors';
import PolarisIcon from './PolarisIcon';
import { formatInTimeZone } from 'date-fns-tz';

type DateTimeDisplayProps = HTMLAttributes<HTMLDivElement> & {
  children: string | null;
  singleLine?: boolean;
  missingText?: string | null;
  showTZ?: boolean;
};

const DateTimeDisplay = ({ children, singleLine = false, missingText = null, showTZ = false, ...props }: DateTimeDisplayProps) => {
  const [dateTime, setDateTime] = useState<{ date: string | null; time: string | null }>({ date: null, time: null });
  const authLoading = useAppSelector(selectAuthLoading);
  const tz = children ? formatInTimeZone(new Date(children), Intl.DateTimeFormat().resolvedOptions().timeZone, 'zzz') : '-';

  useEffect(() => {
    setDateTime({
      date: children ? formatDate(children) : null,
      time: children ? formatTime(children) : null
    });
  }, [authLoading, children]);

  return (
    <Display singleline={+singleLine} {...props}>
      {!children ? (
        missingText
      ) : authLoading ? (
        <PolarisIcon icon="loading_dots" width={20} />
      ) : (
        <>
          <div style={{ display: singleLine ? 'inline-block' : 'block' }}>{dateTime.date}</div>
          <Time singleline={`${singleLine}`}>
            {dateTime.time}
            {showTZ && ` ${tz}`}
          </Time>
        </>
      )}
    </Display>
  );
};

export default DateTimeDisplay;

const Display = styled.div<{ singleline: number }>`
  line-height: ${({ singleline }) => (singleline ? 1 : 1.5)};
  font-size: ${({ singleline }) => (singleline ? '1rem' : '.9rem')};
  min-width: ${({ singleline }) => (singleline ? '130px' : 'none')};
`;

const Time = styled.span<{ singleline: string }>`
  opacity: 0.5;
  margin-left: ${({ singleline }) => (singleline === 'true' ? 5 : 0)}px;
`;
